import { Autocomplete, GridLegacy as Grid, TextField } from '@mui/material';
import { Fragment } from 'react';
import { Control, Controller, useWatch } from 'react-hook-form';

import { CompanyData } from '~/data/company';
import { usePersonnelGroupOptions } from '~/features/RehireDialog/hooks/usePersonnelGroupOptions';
import { useSxStyles } from '~/features/RehireDialog/useStyles';
import {
  PersonnelGroupValues,
  PersonnelGroups,
} from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmploymentDetail/useScheduleEmploymentDetail';

import { OverlappingEmpForm } from '../../hooks/useOverlappingEmploymentForm';

type Props = {
  companyData: CompanyData;
  control: Control<OverlappingEmpForm>;
};

export const PersonnelGroupFormComponents = ({
  companyData,
  control,
}: Props) => {
  const { getPersonnelGroupOptions, getPersonnelGroupName } =
    usePersonnelGroupOptions();
  const sxStyles = useSxStyles();

  const { fComMaxPersonnelGroups } = companyData.config;
  const personnelGroupIndexNumbers: number[] = Array.from(
    Array(fComMaxPersonnelGroups).keys(),
  );

  const RenderPersonnelGroupField = ({
    groupNumber,
  }: {
    groupNumber: number;
  }) => {
    const emplDetailData = useWatch({
      control: control,
      name: 'overlappingEmployment.employmentDetails',
    });
    const personnelGroupValues: PersonnelGroupValues = emplDetailData
      ? personnelGroupIndexNumbers.reduce(
          (acc, cur) => ({
            ...acc,
            [`PERSONNEL_GROUP_${cur}`]:
              emplDetailData[
                `fEmplDetailPersonnelGroup${cur}` as keyof PersonnelGroups
              ],
          }),
          {},
        )
      : {};
    const fieldName =
      `fEmplDetailPersonnelGroup${groupNumber}` as keyof PersonnelGroups;
    const fieldOptions = getPersonnelGroupOptions(
      groupNumber,
      personnelGroupValues,
    );

    return (
      <Grid item>
        <Controller
          control={control}
          name={`overlappingEmployment.employmentDetails.${fieldName}`}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Autocomplete
              options={fieldOptions}
              getOptionLabel={(o) => o.name}
              isOptionEqualToValue={(o, v) => o.code === v.code}
              onChange={(_, value) => onChange(value?.code || '')}
              value={fieldOptions.find((o) => o.code === value) || null}
              sx={{ ...sxStyles.selectStyle, ...sxStyles.inputBottomMargin }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={getPersonnelGroupName(groupNumber)}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          )}
        />
      </Grid>
    );
  };

  return (
    <>
      {personnelGroupIndexNumbers.map((number) => (
        <Fragment key={number}>
          <RenderPersonnelGroupField groupNumber={number + 1} />
        </Fragment>
      ))}
    </>
  );
};
